import useUiContext from '@groupefungo/react-apollo.core/ui/UseContext';

const useCalendarStyles = () => {
  const { makeStyles } = useUiContext();

  const slotStyle = {
    fontSize: '24px!important',
    fontFamily: 'apercu-medium-pro, sans-serif',
    fontWeight: 600,
    '& .MuiButton-root': {
      fontSize: '24px',
      fontWeight: 600,
      width: '100%',
      color: 'inherit',
      padding: 0,
      margin: '1px 0',
      cursor: 'pointer',
      position: 'relative',
      display: 'grid',
      border: 'none!important',
      minHeight: 66,
    },
    '& .MuiButton-label': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    '& .Mui-disabled': {
      color: '#7e8083!important',
      borderColor: '#7e8083!important',
      textDecoration: 'line-through',
    },
  };

  const sliderStyle = {
    top: 20,
    left: 0,
    content: '',
    position: 'absolute',
    width: 30,
    height: 30,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    cursor: 'pointer',
    zIndex: 2,
    '@media only screen and (min-width: 1025px)': {
      left: -50,
    },
    '@media only screen and (min-device-width: 768px) and (max-device-width: 1024px)':
      {
        top: 100,
      },
  };

  const useStyles = makeStyles({
    calendarContainer: {
      overflow: 'hidden',
    },
    calendarColumnHeader: {
      padding: '6px 0',
      display: 'inline-block',
      width: '100%',
      backgroundColor: '#e2e6e5',
      position: 'sticky',
      top: 0,
      zIndex: 1,
      textAlign: 'center',
      marginTop: 11,
      paddingTop: 15,

      '@media only screen and (min-device-width: 768px) and (max-device-width: 1024px)':
        {
          paddingTop: '3em',
        },
      '&& .MuiButton-root': {
        fontSize: 24,
      },
    },
    calendarColumnHeaderMonth: {
      fontFamily: 'apercu-medium-pro',
      color: '#8a9b97',
      fontSize: 18,
      fontWeight: 800,
      textTransform: 'uppercase',
      display: 'block',
      margin: '15px 0',
    },
    calendarColumnHeaderDay: {
      fontFamily: 'apercu-medium-pro',
      color: '#01372c',
      fontSize: 15,
      fontWeight: 800,
      textTransform: 'uppercase',
      display: 'block',

      '@media only screen and (min-device-width: 768px) and (max-device-width: 1024px)':
        {
          paddingTop: 45,
        },
    },
    calendarColumnHeaderDate: {
      color: '#8a9b97',
      fontSize: 12,
      fontWeight: 600,
      display: 'block',
    },
    calendarColumnToday: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      height: 'auto',
      flex: 1,
      backgroundColor: '#0b372c2b',
    },
    calendarColumn: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      height: 'auto',
      flex: 1,
      gap: '5px',
    },
    calendarColumnWithSideHours: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      height: 'auto',
      flex: 1,
      gap: '0px',
    },

    slotUnavailable: {
      ...slotStyle,
      color: 'rgba(155,155,155,0.5)',
      border: '2px solid rgba(155,155,155,0.5)',
      textDecoration: 'line-through',
      backgroundColor: 'rgba(255,255,255,0.4)',
    },
    slotAvailable: {
      ...slotStyle,
      color: '#01372c',
      border: '2px solid #01372c',
      backgroundColor: 'rgba(255,255,255,0.4)',
      '&:hover': {
        transform: 'scale(1.02)',
      },
    },
    slotReserved: {
      ...slotStyle,
      color: '#01372c',
      border: '2px solid #01372c',
      borderLeft: '3px solid #01372c',
      '&:hover': {
        transform: 'scale(1.02)',
      },
      '&::before': {
        content: '"↪"',
        position: 'absolute',
        left: '6px',
        top: '35%',
        fontSize: '14px',
        color: '#01372c',
        fontWeight: 'bold',
        opacity: 0.7,
        transition: 'opacity 0.2s ease-in-out',
      },
      '& > *': {
        color: '#01372c!important',
      },
    },
    slotTimeOff: {
      ...slotStyle,
      color: '#999',
      border: '2px solid #ccc',
      borderLeft: '3px solid #ccc',
      position: 'relative',
      '&::before': {
        content: '"↪"',
        position: 'absolute',
        left: '6px',
        top: '35%',
        fontSize: '14px',
        color: '#424242',
        fontWeight: 'bold',
        opacity: 0.7,
      },
    },
    // Starting slot styles - pour les créneaux qui génèrent des forward blocks
    slotStartingStandard: {
      ...slotStyle,
      color: '#4a746a',
      border: '2px solid #4a746a',
      borderLeft: '3px solid #4a746a',
      position: 'relative',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'scale(1.02)',
      },
    },
    slotStartingLongAppointment: {
      ...slotStyle,
      color: '#4a746a',
      border: '2px solid #4a746a',
      borderLeft: '3px solid #4a746a',
      position: 'relative',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'scale(1.02)',
      },
      '&::after': {
        content: '"→"',
        position: 'absolute',
        right: '4px',
        top: '45px',
        transform: 'translateY(-50%)',
        fontSize: '16px',
        color: '#4a746a',
        fontWeight: 'bold',
        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
        transition: 'transform 0.2s ease-in-out',
      },
      '&:hover::after': {
        transform: 'translateY(-50%) translateX(2px)',
      },
    },
    slotStartingBooked: {
      ...slotStyle,
      color: '#01372c!important',
      border: '2px solid #01372c',
      borderLeft: '3px solid #01372c',
      position: 'relative',
      boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
      '&::after': {
        content: '"→"',
        position: 'absolute',
        right: '4px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '16px',
        color: '#01372c',
        fontWeight: 'bold',
        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
      },
    },
    slotStartingTimeOff: {
      ...slotStyle,
      color: '#424242',
      border: '2px solid #424242',
      borderLeft: '3px solid #424242',
      position: 'relative',
      boxShadow: '0 2px 8px rgba(66, 66, 66, 0.3)',
      '&::after': {
        content: '"→"',
        position: 'absolute',
        right: '4px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '16px',
        color: '#424242',
        fontWeight: 'bold',
        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
      },
    },

    // Forward-block styles pour grouper visuellement les créneaux ensemble
    // Appointments taken off a stylist/fitter by Clear schedule: styled like a
    // free slot (start with "→", following slots with "↪") but red. Text
    // #7a1a12 on #fdecea is 9.26:1, border #b3261e on white 6.54:1.
    slotNeedsReassignmentStart: {
      ...slotStyle,
      color: '#7a1a12',
      backgroundColor: '#fdecea',
      border: '2px solid #b3261e',
      borderLeft: '3px solid #b3261e',
      position: 'relative',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'scale(1.02)',
      },
      '&::after': {
        content: '"→"',
        position: 'absolute',
        right: '4px',
        top: '45px',
        transform: 'translateY(-50%)',
        fontSize: '14px',
        color: '#7a1a12',
        fontWeight: 'bold',
      },
    },
    slotNeedsReassignmentContinued: {
      ...slotStyle,
      color: '#7a1a12',
      backgroundColor: '#fdecea',
      border: '2px solid #b3261e',
      borderLeft: '3px solid #b3261e',
      position: 'relative',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'scale(1.02)',
      },
      '&::before': {
        content: '"↪"',
        position: 'absolute',
        left: '6px',
        top: '4px',
        fontSize: '14px',
        color: '#7a1a12',
        fontWeight: 'bold',
      },
    },
    slotForwardBlockStandard: {
      ...slotStyle,
      color: '#01372c',
      border: '2px solid #01372c',
      borderLeft: '3px solid #01372c',
      position: 'relative',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'scale(1.02)',
      },
      '&::before': {
        content: '"↪"',
        position: 'absolute',
        left: '6px',
        top: '4px',
        fontSize: '14px',
        color: '#01372c',
        fontWeight: 'bold',
        opacity: 0.7,
        transition: 'opacity 0.2s ease-in-out',
      },
      '&:hover::before': {
        opacity: 1,
      },
    },
    slotForwardBlockLongAppointment: {
      ...slotStyle,
      color: '#4a746a',
      border: '2px solid #4a746a',
      borderLeft: '3px solid #4a746a',
      position: 'relative',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'scale(1.02)',
      },
      '&::before': {
        content: '"↪"',
        position: 'absolute',
        left: '6px',
        top: '35%',
        fontSize: '14px',
        color: '#4a746a',
        fontWeight: 'bold',
        opacity: 0.7,
        transition: 'opacity 0.2s ease-in-out',
      },
      '&:hover::before': {
        opacity: 1,
      },
    },
    slotForwardBlockBooked: {
      ...slotStyle,
      color: '#4a746a',
      border: '2px solid #2e7d32',
      borderLeft: '4px solid #2e7d32',
      borderBottom: '4px solid #2e7d32',
      position: 'relative',
      '&:hover': {
        transform: 'scale(1.02)',
      },
      '&::before': {
        content: '"↪"',
        position: 'absolute',
        left: '6px',
        top: '4px',
        fontSize: '14px',
        color: '#4a746a',
        fontWeight: 'bold',
        opacity: 0.7,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '-2px',
        top: '0',
        bottom: '0',
        width: '4px',
        borderRadius: '0 2px 2px 0',
      },
    },
    slotForwardBlockTimeOff: {
      ...slotStyle,
      color: '#424242',
      border: '2px solid #424242',
      borderLeft: '3px solid #424242',
      position: 'relative',
      '&::before': {
        content: '"↪"',
        position: 'absolute',
        left: '6px',
        top: '4px',
        fontSize: '14px',
        color: '#424242',
        fontWeight: 'bold',
        opacity: 0.7,
      },
      '&:hover': {
        transform: 'scale(1.02)',
      },
    },
    addFreeTimeBtns: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr auto',
      alignItems: 'center',
      padding: '10px',
      gap: '10px',
      '& .MuiButton-root': {
        fontSize: 12,
        textTransform: 'none',
        border: '1px solid!important',
        borderColor: '#01372c',
        height: 38,
        minWidth: 38,
        minHeight: 38,
        color: '#01372c',
      },
    },
    appointmentTypeDisplay: {
      fontSize: '10px',
      fontWeight: 'bold',
      backgroundColor: 'rgba(255,255,255,0.2)',
      padding: '2px 4px',
      borderRadius: '8px',
      marginTop: '4px',

    },
    infoButton: {
      fontSize: '12px!important',
      backgroundColor: '#e2e6e5!important',
      borderRadius: '50%!important',
    },
    removeBtn: {
      border: 'none!important',
    },
    slider: {
      position: 'relative',
    },
    sliderNext: {
      ...sliderStyle,
      right: 0,
    },
    sliderPrevious: {
      ...sliderStyle,
      left: 0,
    },
    calendarBox: {
      textAlign: 'center',
      maxHeight: '100%',
      height: 'auto',
      boxShadow: '0 17px 12px -14px rgba(0,0,0,0.06)',
      borderBottom: '0 solid rgba(255,255,255,0)',
      display: 'flex',
      position: 'relative',
      backgroundColor: '#fff',
      zIndex: 0,
    },
    dayNumber: {
      fontSize: 14,
    },
    dayNameTile: {
      textAlign: 'center',
    },
    gridListWrapper: {
      border: '1px solid #01372c',
      borderRight: 0,
      borderTop: 0,
    },
    calendarTile: {
      border: '1px solid #01372c',
      borderBottom: 0,
      borderLeft: 0,
      width: 'auto',
      padding: '0 !important',
    },
    monthCard: {
      height: '100%',
      borderRadius: 0,
    },
    monthCardContent: {
      height: '100%',
      padding: 0,
    },
    selected: {
      backgroundColor: '#01372c !important',
      color: '#fff !important',
    },
  });

  const classes = useStyles();

  const getSlotType = (status, stylistId, isPast) => {
    if (isPast) {
      return classes.slotUnavailable;
    } if (stylistId === null) {
      return classes.slotAvailable;
    }
    switch (status) {
      case 'free':
        return classes.slotAvailable;
      case 'booked':
        return classes.slotReserved;
      case 'timeoff':
        return classes.slotTimeOff;

        // Starting slot styles (slots qui génèrent des forward blocks)
      case 'starting-standard':
        return classes.slotStartingStandard;
      case 'starting-long-appointment':
        return classes.slotStartingLongAppointment;
      case 'starting-booked':
        return classes.slotStartingBooked;
      case 'starting-timeoff':
        return classes.slotStartingTimeOff;

        // Forward-block styles
      case 'standard':
        return classes.slotForwardBlockStandard;
      case 'long-appointment':
        return classes.slotForwardBlockLongAppointment;
      case 'forward-block-booked':
        return classes.slotForwardBlockBooked;
      case 'forward-block-timeoff':
        return classes.slotForwardBlockTimeOff;
      default:
        return classes.slotUnavailable;
    }
  };

  return {
    classes,
    getSlotType,
  };
};

export default useCalendarStyles;
